import { useState, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import { TradeType } from '@/lib/damageTypes';

export interface ScoutBookmark {
  id: string;
  timestamp: number; // seconds into video
  gps?: { lat: number; lng: number };
  trade?: TradeType;
  damageType?: string;
  voiceNote?: Blob;
}

export const useFieldScout = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [duration, setDuration] = useState(0);
  const [bookmarks, setBookmarks] = useState<ScoutBookmark[]>([]);
  const [previewStream, setPreviewStream] = useState<MediaStream | null>(null);
  
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startTimeRef = useRef<number>(0);
  const durationIntervalRef = useRef<number | null>(null);

  const getSupportedMimeType = () => {
    const types = [
      'video/webm;codecs=vp9',
      'video/webm;codecs=vp8',
      'video/webm',
      'video/mp4'
    ];
    return types.find(type => MediaRecorder.isTypeSupported(type)) || 'video/webm';
  };

  const startRecording = useCallback(async () => {
    try {
      // Check if camera is supported
      if (!navigator.mediaDevices?.getUserMedia) {
        toast.error('Camera not supported on this device');
        return;
      }

      // Request back camera with high quality
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: 'environment',
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        },
        audio: true 
      });

      const mimeType = getSupportedMimeType();
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType
      });

      chunksRef.current = [];
      startTimeRef.current = Date.now();

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.start(1000); // Capture chunks every 1 second
      mediaRecorderRef.current = mediaRecorder;
      setPreviewStream(stream);
      setIsRecording(true);
      setBookmarks([]);
      setDuration(0);

      // Update duration every second
      durationIntervalRef.current = window.setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
        setDuration(elapsed);
      }, 1000);

      toast.success('Field Scout started');
    } catch (error) {
      console.error('Error starting Field Scout:', error);
      toast.error('Could not access camera');
    }
  }, []);

  const stopRecording = useCallback((): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const mediaRecorder = mediaRecorderRef.current;
      if (!mediaRecorder || mediaRecorder.state === 'inactive') {
        reject(new Error('No active recording'));
        return;
      }

      mediaRecorder.onstop = () => {
        setIsRecording(false);
        
        if (durationIntervalRef.current) {
          clearInterval(durationIntervalRef.current);
          durationIntervalRef.current = null;
        }

        const videoBlob = new Blob(chunksRef.current, { type: 'video/webm' });
        
        // Stop all tracks
        mediaRecorder.stream.getTracks().forEach(track => track.stop());
        setPreviewStream(null);
        
        toast.success('Recording saved');
        resolve(videoBlob);
      };

      mediaRecorder.stop();
    });
  }, []);

  const addBookmark = useCallback((trade?: TradeType, damageType?: string) => {
    if (!isRecording) return;

    const currentTimestamp = Math.floor((Date.now() - startTimeRef.current) / 1000);
    
    // Get GPS if available
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const bookmark: ScoutBookmark = {
            id: `${Date.now()}-${Math.random()}`,
            timestamp: currentTimestamp,
            gps: {
              lat: position.coords.latitude,
              lng: position.coords.longitude
            },
            trade,
            damageType
          };
          setBookmarks(prev => [...prev, bookmark]);
        },
        () => {
          // GPS failed, add without location
          const bookmark: ScoutBookmark = {
            id: `${Date.now()}-${Math.random()}`,
            timestamp: currentTimestamp,
            trade,
            damageType
          };
          setBookmarks(prev => [...prev, bookmark]);
        }
      );
    } else {
      const bookmark: ScoutBookmark = {
        id: `${Date.now()}-${Math.random()}`,
        timestamp: currentTimestamp,
        trade,
        damageType
      };
      setBookmarks(prev => [...prev, bookmark]);
    }

    toast.success('Damage marked');
  }, [isRecording]);

  const removeBookmark = useCallback((id: string) => {
    setBookmarks(prev => prev.filter(b => b.id !== id));
  }, []);

  const cancelRecording = useCallback(() => {
    const mediaRecorder = mediaRecorderRef.current;
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      mediaRecorder.stop();
      mediaRecorder.stream.getTracks().forEach(track => track.stop());
      setIsRecording(false);
      setPreviewStream(null);
      setBookmarks([]);
      setDuration(0);
      
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
        durationIntervalRef.current = null;
      }
      
      toast.info('Recording cancelled');
    }
  }, []);

  return {
    isRecording,
    duration,
    bookmarks,
    previewStream,
    startRecording,
    stopRecording,
    addBookmark,
    removeBookmark,
    cancelRecording
  };
};
